import { Route, ServiceBikeType, populateRoute } from "@repo/common"
import { toast } from "@repo/ui/hooks/use-toast"
import { useTranslateApiError } from "@repo/ui/lib/translate-api-error"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslations } from "use-intl"
import { request } from "../api"

export interface ServiceReminderEmail {
  id: string
  customerEmail: string
  status: "PENDING" | "SENT"
  emailType: "FIRST" | "FOLLOW_UP"
  totalPrice: number
  discountAmount: number
  discountCode: string | null
  sentAt: string | null
  createdAt: string
  job: {
    id: string
    handInDate: string
    customer: {
      firstName: string
      lastName: string | null
      phoneNumber: string
    } | null
    bike: {
      color: string | null
      type: ServiceBikeType | null
      brand: string | null
      model: string | null
    } | null
  }
  parts: Array<{
    id: string
    estimatedGrade: number
    action: string
    laborPrice: number | null
    part: {
      id: string
      name: string
      image: string
    }
  }>
}

export interface PaginatedServiceReminderEmails {
  items: ServiceReminderEmail[]
  totalCount: number
  page: number
  pageSize: number
}

async function fetchEmails(
  storeId: string,
  filters: EmailFilters,
): Promise<PaginatedServiceReminderEmails> {
  return request<PaginatedServiceReminderEmails>({
    url: populateRoute(Route.SERVICE_REMINDER__FIND_BY_STORE, storeId),
    method: "GET",
    params: {
      status: filters.status,
      page: filters.page,
      "page-size": filters.pageSize,
      sort: filters.sort,
      ...(filters.search ? { search: filters.search } : {}),
    },
  })
}

async function sendBatch(
  storeId: string,
  emailIds: string[],
): Promise<{
  sent: number
  removed: number
  skipped: number
  failed: number
}> {
  return request<{
    sent: number
    removed: number
    skipped: number
    failed: number
  }>({
    url: populateRoute(Route.SERVICE_REMINDER__SEND_BATCH, storeId),
    method: "POST",
    data: { emailIds },
  })
}

async function dismissBatch(
  storeId: string,
  emailIds: string[],
): Promise<{ dismissed: number; skipped: number }> {
  return request<{ dismissed: number; skipped: number }>({
    url: populateRoute(Route.SERVICE_REMINDER__DISMISS_BATCH, storeId),
    method: "POST",
    data: { emailIds },
  })
}

async function populateStore(storeId: string): Promise<{
  created: number
  updated: number
  removed: number
  followUps: number
}> {
  return request<{
    created: number
    updated: number
    removed: number
    followUps: number
  }>({
    url: populateRoute(Route.SERVICE_REMINDER__POPULATE_STORE, storeId),
    method: "POST",
  })
}

export function useServiceReminderMutations(storeId: string) {
  const queryClient = useQueryClient()
  const translateApiError = useTranslateApiError()
  const t = useTranslations("alfred.lib.serviceReminders")

  const invalidateQueries = async () => {
    // Every page/search/sort permutation is its own cache entry, so invalidate
    // the whole list prefix rather than two exact filter objects.
    await queryClient.invalidateQueries({
      queryKey: serviceReminderQueries.lists(),
    })
  }

  const sendBatchMutation = useMutation({
    mutationFn: (emailIds: string[]) => sendBatch(storeId, emailIds),
    onSuccess: ({ removed, skipped, failed }) => {
      if (removed > 0) {
        toast({
          title: t("notSentNothingToFix", { count: removed }),
        })
      }
      if (skipped > 0) {
        toast({
          title: t("alreadySentOrRemoved", { count: skipped }),
        })
      }
      if (failed > 0) {
        toast({
          title: t("sendFailedCount", { count: failed }),
          description: t("tryAgain"),
          variant: "destructive",
        })
      }
    },
    onError: (error) => {
      toast({
        title: t("sendFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: invalidateQueries,
  })

  const dismissBatchMutation = useMutation({
    mutationFn: (emailIds: string[]) => dismissBatch(storeId, emailIds),
    onSuccess: ({ dismissed, skipped }) => {
      if (dismissed > 0) {
        toast({
          title: t("dismissed", { count: dismissed }),
        })
      }
      if (skipped > 0) {
        toast({
          title: t("alreadySentOrRemoved", { count: skipped }),
        })
      }
    },
    onError: (error) => {
      toast({
        title: t("dismissFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: invalidateQueries,
  })

  const populateStoreMutation = useMutation({
    mutationFn: () => populateStore(storeId),
    onSuccess: ({ removed }) => {
      if (removed === 0) return
      toast({
        title: t("removedNothingToFix", { count: removed }),
      })
    },
    onError: (error) => {
      toast({
        title: t("generateFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: invalidateQueries,
  })

  return { sendBatchMutation, dismissBatchMutation, populateStoreMutation }
}

interface ServiceReminderSettings {
  id: string
  storeId: string
  heroImageUrl: string | null
  followUpHeroImageUrl: string | null
  firstEmailDiscountPercent: number | null
  firstEmailDiscountAppliesTo: string | null
  followUpEmailDiscountPercent: number | null
  followUpEmailDiscountAppliesTo: string | null
  firstEmailDiscountNumDays: number | null
  firstEmailDiscountEndDate: string | null
  followUpEmailDiscountNumDays: number | null
  followUpEmailDiscountEndDate: string | null
}

interface ServiceReminderModule {
  id: string
  storeId: string
  partId: string | null
  title: string
  text: string
  imageUrl: string | null
  buttonText: string | null
  buttonUrl: string | null
  part: {
    id: string
    name: string
    image: string
  } | null
}

interface SettingsResponse {
  settings: ServiceReminderSettings | null
  modules: ServiceReminderModule[]
  sendingEmail: string
}

async function fetchSettings(storeId: string): Promise<SettingsResponse> {
  return request<SettingsResponse>({
    url: populateRoute(Route.SERVICE_REMINDER__GET_SETTINGS, storeId),
    method: "GET",
  })
}

async function updateSettings(
  storeId: string,
  data: {
    heroImageUrl?: string | null
    followUpHeroImageUrl?: string | null
    firstEmailDiscountPercent?: number | null
    firstEmailDiscountAppliesTo?: string | null
    followUpEmailDiscountPercent?: number | null
    followUpEmailDiscountAppliesTo?: string | null
    firstEmailDiscountNumDays?: number | null
    firstEmailDiscountEndDate?: string | null
    followUpEmailDiscountNumDays?: number | null
    followUpEmailDiscountEndDate?: string | null
  },
): Promise<ServiceReminderSettings> {
  return request<ServiceReminderSettings>({
    url: populateRoute(Route.SERVICE_REMINDER__UPDATE_SETTINGS, storeId),
    method: "PATCH",
    data,
  })
}

async function upsertModule(
  storeId: string,
  data: {
    partId: string | null
    title: string
    text: string
    imageUrl?: string | null
    buttonText?: string | null
    buttonUrl?: string | null
  },
): Promise<ServiceReminderModule> {
  return request<ServiceReminderModule>({
    url: populateRoute(Route.SERVICE_REMINDER__UPSERT_MODULE, storeId),
    method: "PUT",
    data,
  })
}

async function deleteModule(storeId: string, moduleId: string): Promise<void> {
  return request<void>({
    url: populateRoute(
      Route.SERVICE_REMINDER__DELETE_MODULE,
      storeId,
      moduleId,
    ),
    method: "DELETE",
  })
}

async function uploadImage(
  storeId: string,
  file: File,
): Promise<{ imageUrl: string }> {
  const formData = new FormData()
  formData.append("image", file)
  return request<{ imageUrl: string }>({
    url: populateRoute(Route.SERVICE_REMINDER__UPLOAD_IMAGE, storeId),
    method: "POST",
    data: formData,
  })
}

export function useServiceReminderSettingsMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateSettings = async () => {
    await queryClient.invalidateQueries(
      serviceReminderQueries.settings(storeId),
    )
  }

  const updateSettingsMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateSettings>[1]) =>
      updateSettings(storeId, data),
    onSettled: invalidateSettings,
  })

  const upsertModuleMutation = useMutation({
    mutationFn: (data: Parameters<typeof upsertModule>[1]) =>
      upsertModule(storeId, data),
    onSettled: invalidateSettings,
  })

  const deleteModuleMutation = useMutation({
    mutationFn: (moduleId: string) => deleteModule(storeId, moduleId),
    onSettled: invalidateSettings,
  })

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => uploadImage(storeId, file),
  })

  return {
    updateSettingsMutation,
    upsertModuleMutation,
    deleteModuleMutation,
    uploadImageMutation,
  }
}

async function fetchEmailPreview(
  storeId: string,
  emailId: string,
): Promise<{ html: string | null }> {
  return request<{ html: string | null }>({
    url: populateRoute(Route.SERVICE_REMINDER__PREVIEW_EMAIL, storeId, emailId),
    method: "POST",
  })
}

export interface EmailFilters {
  status: "PENDING" | "SENT"
  page: number
  pageSize: number
  search?: string
  sort: "asc" | "desc"
}

export const EMAIL_PAGE_SIZE = 50

export const DEFAULT_EMAIL_FILTERS: EmailFilters = {
  status: "PENDING",
  page: 1,
  pageSize: EMAIL_PAGE_SIZE,
  sort: "desc",
}

const serviceReminderQueries = {
  all: () => ["service-reminders"],
  lists: () => [...serviceReminderQueries.all(), "list"],
  list: (storeId: string, filters: EmailFilters) =>
    queryOptions({
      queryKey: [...serviceReminderQueries.lists(), storeId, filters],
      queryFn: () => fetchEmails(storeId, filters),
    }),
  settings: (storeId: string) =>
    queryOptions({
      queryKey: [...serviceReminderQueries.all(), "settings", storeId],
      queryFn: () => fetchSettings(storeId),
    }),
  emailPreview: (storeId: string, emailId: string) =>
    queryOptions({
      queryKey: [...serviceReminderQueries.all(), "preview", storeId, emailId],
      queryFn: () => fetchEmailPreview(storeId, emailId),
      enabled: !!emailId,
      // Every open re-generates a pending email on the backend, so never
      // reuse a cached preview and never re-run it on retry, window focus or
      // reconnect
      staleTime: 0,
      gcTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
}

export default serviceReminderQueries
